import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { OcrProduct } from 'composables/ocr/types';

export const AMOUNT_DECIMAL_PLACES = 2;
export const PRECISION_FACTOR = 10 ** AMOUNT_DECIMAL_PLACES;

const toFloat = (value: number | string | null | undefined): number => {
    const normalized = String(value ?? '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const round = (value: number): number => {
    return Math.round(value * PRECISION_FACTOR) / PRECISION_FACTOR;
};

const parseQuantity = (quantity: number | string | null | undefined): number => {
    if (!quantity) {
        return 1;
    }
    const parsed = typeof quantity === 'number' ? quantity : parseFloat(quantity);
    return Number.isNaN(parsed) ? 1 : parsed;
};

const computeTaxAmountForRate = (
    products: OcrProduct[],
    taxId: number,
    taxRate: string | number,
): number => {
    const rate = Number(taxRate);

    const totalWithoutTaxInCents = products
        .filter((product) => product.taxRateId === taxId)
        .reduce((sum, product) => {
            const qty = parseQuantity(product.quantity);
            const priceWithoutTax = toFloat(product.priceWithoutTax || 0);
            return sum + Math.round(priceWithoutTax * qty * PRECISION_FACTOR);
        }, 0);

    return Math.round((totalWithoutTaxInCents * rate) / 100);
};

export const calculateTotalWithoutTax = (products: OcrProduct[]): number => {
    const total = products.reduce((sum, { priceWithoutTax, quantity }) => {
        const qty = parseQuantity(quantity);
        const amount = toFloat(priceWithoutTax);
        const amountInCents = Math.round(amount * qty * PRECISION_FACTOR);
        return sum + amountInCents / PRECISION_FACTOR;
    }, 0);

    return round(total);
};

export const calculateTotalWithTax = (
    totalWithoutTax: number | null,
    totalTax: number | null,
): number => {
    const withoutTaxInCents = Math.round(toFloat(totalWithoutTax) * PRECISION_FACTOR);
    const taxInCents = Math.round(toFloat(totalTax) * PRECISION_FACTOR);

    return round((withoutTaxInCents + taxInCents) / PRECISION_FACTOR);
};

export const calculateTotalTax = (products: OcrProduct[], taxRows: TaxRate[]): number => {
    let overrideTaxInCents = 0;
    const nonOverriddenProducts: OcrProduct[] = [];

    for (const product of products) {
        if (product.taxAmountOverride !== null && product.taxAmountOverride !== undefined) {
            const parsed = parseFloat(String(product.taxAmountOverride).replace(',', '.'));
            if (!Number.isNaN(parsed)) {
                overrideTaxInCents += Math.round(parsed * PRECISION_FACTOR);
                continue;
            }
        }
        nonOverriddenProducts.push(product);
    }

    const rateTaxInCents = taxRows.reduce((sum, taxItem) => {
        return sum + computeTaxAmountForRate(nonOverriddenProducts, taxItem.id, taxItem.rate);
    }, 0);

    return round((overrideTaxInCents + rateTaxInCents) / PRECISION_FACTOR);
};

export const getUniqueTaxRows = (products: OcrProduct[], taxList: TaxRate[]): TaxRate[] => {
    const seenIds = new Set<number>();
    const taxes: TaxRate[] = [];

    for (const product of products) {
        if (
            product.taxRateId === null ||
            product.taxRateId === undefined ||
            seenIds.has(product.taxRateId)
        ) {
            continue;
        }
        const taxItem = taxList.find((tax) => tax.id === product.taxRateId);
        if (taxItem) {
            seenIds.add(taxItem.id);
            taxes.push(taxItem);
        }
    }

    return taxes;
};

export const calculateTotalByTax = (
    products: OcrProduct[],
    taxId: number,
    taxRate: string | number,
): number => {
    let overrideInCents = 0;
    const nonOverridden: OcrProduct[] = [];

    for (const product of products) {
        if (product.taxRateId !== taxId) {
            continue;
        }
        if (product.taxAmountOverride !== null && product.taxAmountOverride !== undefined) {
            const parsed = parseFloat(String(product.taxAmountOverride).replace(',', '.'));
            if (!Number.isNaN(parsed)) {
                overrideInCents += Math.round(parsed * PRECISION_FACTOR);
                continue;
            }
        }
        nonOverridden.push(product);
    }

    return round(
        (overrideInCents + computeTaxAmountForRate(nonOverridden, taxId, taxRate)) /
            PRECISION_FACTOR,
    );
};

const DEFAULT_EMPTY_PRODUCT: Omit<OcrProduct, 'accountingCode' | 'taxRateId'> = {
    description: '',
    quantity: 1,
    priceWithoutTax: null,
    unit: '',
    catalogueLink: null,
    analyticCode: '',
    stockOutDate: null,
    warehouseId: '',
    serialNumbers: '',
    taxAmountOverride: null,
    amountIncludingTaxOverride: null,
};

export interface CreateEmptyProductOptions {
    defaultAccountingCode?: number | null;
    defaultTaxRateId?: number | null;
    supplierId?: number | null;
    companyAccountingCode?: number | null;
}

export const createEmptyProduct = ({
    defaultAccountingCode = null,
    defaultTaxRateId = null,
    supplierId = null,
    companyAccountingCode = null,
}: CreateEmptyProductOptions = {}): OcrProduct => ({
    ...DEFAULT_EMPTY_PRODUCT,
    accountingCode: supplierId === null ? defaultAccountingCode : companyAccountingCode,
    taxRateId: defaultTaxRateId,
});

/**
 * Limits an amount input value to 2 decimal places.
 * Accepts both comma and dot as decimal separator.
 */
export const limitToTwoDecimals = (value: string | number | null): string | number | null => {
    if (value === null || value === '' || value === '-') {
        return value;
    }
    const str = String(value);
    const separator = str.includes(',') ? ',' : '.';
    const separatorIndex = str.indexOf(separator);
    if (separatorIndex !== -1 && str.length - separatorIndex - 1 > 2) {
        return str.substring(0, separatorIndex + 3);
    }
    return str;
};

/**
 * Formats a field value to exactly 2 decimal places with comma separator.
 */
export const formatFieldToTwoDecimals = <
    TKey extends string,
    TObject extends { [P in TKey]: string | number | null },
>(
    object: TObject,
    field: TKey,
): void => {
    const value = object[field];
    if (value !== null && value !== '') {
        const parsed =
            typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
        if (!Number.isNaN(parsed)) {
            (object as { [P in TKey]: string })[field] = parsed.toFixed(2).replace('.', ',');
        }
    }
};

export const setProductDescriptionFromAccountingCode = (
    product: OcrProduct,
    accountingCodes: AccountingCode[],
): void => {
    const hasCustomDescription =
        product.description && !accountingCodes.some((code) => code.label === product.description);
    if (hasCustomDescription) {
        return;
    }

    const matchingCode = accountingCodes.find((code) => code.id === product.accountingCode);
    product.description = matchingCode?.label ?? '';
};
